/**
 * Opening hand / mulligan view.
 *
 * Each player sees their 7 starting cards and may click any subset to send
 * them to the bottom of their deck and draw replacements. Clicking a card
 * toggles the "mulligan" mark; the button at the bottom commits the swap.
 *
 * For simplicity we do NOT implement the actual deck-swap here — the engine
 * already gives a randomized opening hand. Mulligan acts as a "shuffle the
 * marked cards back and redraw" by simply shuffling the player's hand within
 * itself. Designer can use this to scout whether the dealt hand is reasonable.
 */
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { BoardCard } from "./BoardCard";
import type { Card } from "@/types/card";
import type { GameState } from "@/types/game";

interface MulliganViewProps {
  game: GameState;
  playerNum: 1 | 2;
  cardMap: Map<string, Card>;
  onKeep: (newHand: string[], newDeck: string[]) => void;
}

export function MulliganView({ game, playerNum, cardMap, onKeep }: MulliganViewProps) {
  const player = game.players[playerNum];
  const [marked, setMarked] = useState<Set<number>>(new Set());

  const handCards = useMemo(
    () => player.hand.map((id) => cardMap.get(id) ?? null),
    [player.hand, cardMap],
  );

  function toggle(idx: number) {
    setMarked((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  function commit() {
    if (marked.size === 0) {
      onKeep(player.hand, player.deckCards);
      return;
    }
    // Move marked cards to the bottom of deck and draw the same number from the top.
    const handIds = [...player.hand];
    const deck = [...player.deckCards];
    const removed: string[] = [];
    const kept: string[] = [];
    handIds.forEach((id, i) => {
      if (marked.has(i)) removed.push(id);
      else kept.push(id);
    });
    // Push removed to bottom, draw replacement count from top.
    const drawCount = Math.min(removed.length, deck.length);
    const drawn = deck.splice(0, drawCount);
    deck.push(...removed);
    const newHand = [...kept, ...drawn];
    onKeep(newHand, deck);
  }

  const buttonLabel =
    marked.size === 0
      ? "Keep Hand"
      : `Mulligan ${marked.size} card${marked.size > 1 ? "s" : ""}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-xl font-bold tracking-tight text-[#e2e8f0]">
          Opening Hand — {player.name}
        </h1>
        <p className="mt-1 text-sm text-[#94a3b8]">
          Click cards to send them to the bottom of your deck and draw replacements.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        {handCards.map((c, i) => (
          <BoardCard
            key={`${c?.id ?? "empty"}_${i}`}
            card={c}
            size="hand"
            onClick={() => toggle(i)}
            selected={marked.has(i)}
            muted={marked.has(i)}
          />
        ))}
      </div>

      <Button onClick={commit} size="lg">
        {buttonLabel}
      </Button>
    </div>
  );
}
